export type EventListener<TPayload> = (payload: TPayload) => void;

export class EventEmitter<TEvent extends string, TPayloadMap extends Record<TEvent, unknown>> {
  private readonly listeners = new Map<TEvent, Set<EventListener<unknown>>>();

  on<T extends TEvent>(event: T, listener: EventListener<TPayloadMap[T]>): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener as EventListener<unknown>);
    return this;
  }

  off<T extends TEvent>(event: T, listener: EventListener<TPayloadMap[T]>): this {
    const set = this.listeners.get(event);
    if (set) {
      set.delete(listener as EventListener<unknown>);
      if (set.size === 0) {
        this.listeners.delete(event);
      }
    }
    return this;
  }

  emit<T extends TEvent>(event: T, payload: TPayloadMap[T]): this {
    const set = this.listeners.get(event);
    if (!set) {
      return this;
    }
    for (const listener of Array.from(set)) {
      try {
        (listener as EventListener<TPayloadMap[T]>)(payload);
      } catch (error) {
        setTimeout(() => {
          throw error;
        });
      }
    }
    return this;
  }

  removeAllListeners(): void {
    this.listeners.clear();
  }
}
